import appDataService, { Club, Pub, Shop, Series, League, Association } from './appDataService';
import tournamentService from './tournamentService';

// ============================================
// NearbyService – geteilte "In der Nähe"-Logik
// (genutzt von SearchExplorer; Logik analog zur NearbyPage)
// Alle Felder defensiv nullable geparst.
// ============================================

export interface GeoPoint {
  latitude: number;
  longitude: number;
  label?: string | null;
}

export type NearbyItemType = 'tournament' | 'club' | 'pub' | 'shop' | 'series' | 'league' | 'association';

export interface NearbyItem {
  id: string | null;
  name: string | null;
  type: NearbyItemType;
  typeLabel: string;
  slug: string | null;
  image: string | null;
  subtitle: string | null;
  distance: number;
  link: string | null;
}

const TYPE_LABELS: Record<NearbyItemType, string> = {
  tournament: 'Turnier',
  club: 'Verein',
  pub: 'Spielstätte',
  shop: 'Shop',
  series: 'Serie',
  league: 'Liga',
  association: 'Verband',
};

const ROUTE_PREFIXES: Record<NearbyItemType, string> = {
  tournament: '/turnier',
  club: '/verein',
  pub: '/spielstaette',
  shop: '/shop',
  series: '/turnierserie',
  league: '/liga',
  association: '/verband',
};

const toFiniteNumber = (value: any): number | null => {
  if (typeof value === 'number' && isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (isFinite(parsed)) return parsed;
  }
  return null;
};

/** Distanz-Format wie Flutter: "850 m", "12,3 km", "42 km" */
export const formatDistance = (km: number): string => {
  if (!isFinite(km) || km < 0) return '';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1).replace('.', ',')} km`;
  return `${Math.round(km)} km`;
};

/** Turnier-Datum kompakt: "5.7.2026" bzw. "5.7. - 6.7.2026" */
const formatTournamentDates = (dates?: number[] | null): string | null => {
  if (!dates || !Array.isArray(dates)) return null;
  const valid = dates
    .filter((d) => typeof d === 'number' && isFinite(d))
    .slice()
    .sort((a, b) => a - b);
  if (valid.length === 0) return null;
  const first = new Date(valid[0]);
  if (isNaN(first.getTime())) return null;
  if (valid.length === 1) {
    return `${first.getDate()}.${first.getMonth() + 1}.${first.getFullYear()}`;
  }
  const last = new Date(valid[valid.length - 1]);
  if (isNaN(last.getTime())) {
    return `${first.getDate()}.${first.getMonth() + 1}.${first.getFullYear()}`;
  }
  return `${first.getDate()}.${first.getMonth() + 1}. - ${last.getDate()}.${last.getMonth() + 1}.${last.getFullYear()}`;
};

/** Thumbnail für Club/Pub/Shop/Series (Priorität: logo > image > images[0]) */
const entityThumbnail = (item?: Club | Pub | Shop | Series | null): string | null => {
  if (!item || typeof item !== 'object') return null;
  const anyItem = item as any;
  let file: string | null = null;
  if (typeof anyItem.logo === 'string' && anyItem.logo.trim()) {
    file = anyItem.logo.trim();
  } else if (typeof item.image === 'string' && item.image.trim()) {
    file = item.image.trim();
  } else if (
    Array.isArray(item.images) &&
    typeof item.images[0] === 'string' &&
    item.images[0].trim()
  ) {
    file = item.images[0].trim();
  }
  return tournamentService.buildThumbnailUrl(file, 200);
};

/** Profil-Standort aus dem eingeloggten User (defensiv geparst, wie NearbyPage) */
export const getProfileLocation = (user: any): GeoPoint | null => {
  const address = user?.personalData?.address;
  if (!address || typeof address !== 'object') return null;
  const lat = toFiniteNumber(address.latitude);
  const lng = toFiniteNumber(address.longitude);
  if (lat === null || lng === null) return null;
  let label: string | null = null;
  if (typeof address.label === 'string' && address.label.trim()) {
    label = address.label.trim();
  } else {
    const parts = [address.street, address.city, address.country]
      .filter((p: any) => typeof p === 'string' && p.trim())
      .map((p: string) => p.trim());
    label = parts.length > 0 ? parts.join(', ') : 'Profil-Adresse';
  }
  return { latitude: lat, longitude: lng, label };
};

/**
 * Lädt alle 5 Entity-Typen (appData ist 5 Min. client-gecacht) und liefert die
 * nächstgelegenen Einträge gemischt, nach Distanz sortiert.
 */
export const loadNearbyItems = async (
  location: GeoPoint,
  options: { radiusKm?: number; limit?: number } = {},
): Promise<NearbyItem[]> => {
  const { radiusKm = 100, limit = 8 } = options;
  const { latitude: userLat, longitude: userLng } = location;

  const [tournamentRes, clubs, pubs, shops, series, leagues, associations] = await Promise.all([
    tournamentService.getTournaments().catch(() => null),
    appDataService.getClubs().catch(() => [] as Club[]),
    appDataService.getPubs().catch(() => [] as Pub[]),
    appDataService.getShops().catch(() => [] as Shop[]),
    appDataService.getSeries().catch(() => [] as Series[]),
    appDataService.getLeagues().catch(() => [] as League[]),
    appDataService.getAssociations().catch(() => [] as Association[]),
  ]);

  const items: NearbyItem[] = [];

  const pushItem = (
    type: NearbyItemType,
    raw: { id: string | null; name: string | null; slug: string | null; image: string | null; subtitle: string | null },
    lat: number,
    lng: number,
  ) => {
    const distance = tournamentService.calculateDistance(userLat, userLng, lat, lng);
    if (!isFinite(distance) || distance > radiusKm) return;
    items.push({
      ...raw,
      type,
      typeLabel: TYPE_LABELS[type],
      distance,
      link: raw.slug ? `${ROUTE_PREFIXES[type]}/${raw.slug}` : null,
    });
  };

  const tournaments = tournamentService.filterValidTournaments(
    tournamentRes?.data ?? [],
    true,
  );
  for (const t of tournaments) {
    const lat = toFiniteNumber(t?.address?.latitude);
    const lng = toFiniteNumber(t?.address?.longitude);
    if (lat === null || lng === null) continue;
    pushItem(
      'tournament',
      {
        id: (t as any)._id ?? t.id ?? null,
        name: t.name ?? null,
        slug: t.slug ?? null,
        image: tournamentService.getThumbnailUrl(t, 200),
        subtitle: formatTournamentDates(t.dates),
      },
      lat,
      lng,
    );
  }

  const processEntities = (list: (Club | Pub | Shop | Series)[], type: NearbyItemType) => {
    for (const item of Array.isArray(list) ? list : []) {
      if (!item || typeof item !== 'object') continue;
      const lat = toFiniteNumber(item.address?.latitude);
      const lng = toFiniteNumber(item.address?.longitude);
      if (lat === null || lng === null) continue;
      pushItem(
        type,
        {
          id: (item as any)._id ?? item.id ?? null,
          name: item.name ?? null,
          slug: item.slug ?? null,
          image: entityThumbnail(item),
          subtitle:
            typeof item.address?.label === 'string' && item.address.label.trim()
              ? item.address.label.trim()
              : appDataService.formatAddress(item.address),
        },
        lat,
        lng,
      );
    }
  };

  processEntities(clubs, 'club');
  processEntities(pubs, 'pub');
  processEntities(shops, 'shop');
  processEntities(series, 'series');
  processEntities(leagues, 'league');
  processEntities(associations, 'association');

  items.sort((a, b) => a.distance - b.distance);
  return items.slice(0, limit);
};

const nearbyService = {
  getProfileLocation,
  loadNearbyItems,
  formatDistance,
};

export default nearbyService;
